import { Spinner } from '@99designs/common/components';
import Button from 'common/components/Button';
import { Field } from 'common/components/Form';
import { signup } from 'launch/common/xhr/accounts';
import Link from 'launch/components/Link';
import TermsLink from 'launch/components/TermsLink';
import React from 'react';
import { FormattedMessage, intlShape } from 'react-intl';
import Checkbox from './Checkbox';
import Paragraph from './Paragraph';

interface Props {
    title: React.ReactNode;
    onSuccess: () => void;
    email: string;
    onError?: () => void;
    source?: string;
}

interface State {
    errors: { [key: string]: string };
    email: string;
    password: string;
    passwordConfirm: string;
    busy: boolean;
    hasAgreedToPrivacyAndTerms: boolean;
}

class AuthSignupForm extends React.Component<Props, State> {
    static contextTypes = {
        intl: intlShape.isRequired,
    };

    public static defaultProps: Partial<Props> = {
        onError: () => null,
    };

    email: HTMLInputElement | null;
    componentDidMount() {
        if (this.email) {
            this.email.focus();
        }
    }

    constructor(props: Props) {
        super(props);
        this.state = {
            errors: {},
            password: '',
            email: '',
            passwordConfirm: '',
            busy: false,
            hasAgreedToPrivacyAndTerms: false,
        };
    }

    attachTo = <T extends keyof State>(name: T) => {
        return {
            value: this.state[name],
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                this.setState({
                    ...this.state,
                    [name]: e.target.value,
                });
            },
        };
    };

    handlePrivacy = (checked: boolean) => {
        this.setState({ hasAgreedToPrivacyAndTerms: checked });
    };

    handleOnSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const { onSuccess, onError, source } = this.props;
        const { email, password, passwordConfirm, hasAgreedToPrivacyAndTerms } = this.state;
        const t = (id: string) => this.context.intl.formatMessage({ id });

        if (!hasAgreedToPrivacyAndTerms) {
            return this.setState(
                {
                    errors: { email: t('auth_modal.generic_error') },
                },
                onError
            );
        }

        if (password != passwordConfirm) {
            return this.setState(
                {
                    errors: { password: t('auth_modal.password_error') },
                },
                onError
            );
        }

        this.setState({ busy: true });

        try {
            await signup({ email, password, source, hasAgreedToPrivacyAndTerms });
        } catch (errors) {
            return this.setState(
                {
                    errors: errors || { email: t('auth_modal.generic_error') },
                    busy: false,
                },
                onError
            );
        }

        onSuccess();

        this.setState({ busy: false });
    };

    render() {
        const { title } = this.props;
        const t = (id: string) => this.context.intl.formatMessage({ id });

        return (
            <form onSubmit={this.handleOnSubmit} className="modal-form modal-form--wide">
                <div className="modal-form__title">{title}</div>
                <div className="modal-form__input">
                    <Field errorMessage={this.state.errors.email} variations={['collapse']}>
                        <input
                            name="email"
                            placeholder={t('auth_modal.email')}
                            ref={input => (this.email = input)}
                            className="input-text"
                            {...this.attachTo('email')}
                        />
                    </Field>

                    <Field errorMessage={this.state.errors.password} variations={['collapse']}>
                        <input
                            name="password"
                            type="password"
                            placeholder={t('auth_modal.password')}
                            className="input-text"
                            {...this.attachTo('password')}
                        />
                    </Field>

                    <Field>
                        <input
                            ref="confirm"
                            name="passwordConfirm"
                            type="password"
                            placeholder={t('auth_modal.confirm_password')}
                            className="input-text"
                            {...this.attachTo('passwordConfirm')}
                        />
                    </Field>
                </div>

                <div className="modal-form__submit">
                    <Paragraph color="soft">
                        <Checkbox
                            checked={this.state.hasAgreedToPrivacyAndTerms}
                            onChange={d => this.handlePrivacy(d.checked)}
                        >
                            <FormattedMessage
                                id="auth_modal.agree_to_privacy_and_terms"
                                values={{
                                    privacyPolicy: (
                                        <Link
                                            noWrap={true}
                                            href="/legal/privacy"
                                            target="_blank"
                                            variations={['default']}
                                        >
                                            {t('auth_modal.privacy_policy')}
                                        </Link>
                                    ),
                                    terms: (
                                        <Link
                                            noWrap={true}
                                            href="/legal/terms-of-use"
                                            target="_blank"
                                            variations={['default']}
                                        >
                                            {t('auth_modal.terms')}
                                        </Link>
                                    ),
                                }}
                            />
                        </Checkbox>
                    </Paragraph>

                    <Button
                        disabled={!this.state.hasAgreedToPrivacyAndTerms || this.state.busy}
                        typeAttribute="submit"
                        variation={'primary'}
                    >
                        {t('auth_modal.register')}
                    </Button>
                    {this.state.busy && <Spinner variations={['inline', 'login']} />}
                </div>

                <div className="modal-form__footer">
                    <div className="field__message">
                        <span> © 99designs </span>
                        <TermsLink />
                    </div>
                </div>
            </form>
        );
    }
}

export default AuthSignupForm;



// WEBPACK FOOTER //
// ./js/common/components/AuthSignupForm.tsx